import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { createRng } from '../game/core/rng'
import { CRATE_PRICE, KNIVES, knifeById, type KnifeDef } from '../game/data/knives'
import { openCrate as rollCrate, type CrateResult } from '../game/economy/crate'
import type { CutModeId } from '../game/cutting/cutModes'
import { newlyUnlocked, type AchievementContext, type AchievementDef } from '../game/data/achievements'
import { decorById, toggleEquip } from '../game/data/decor'
import { POTATOES, potatoById } from '../game/data/potatoes'
import { upgradeById, upgradePrice, upgradeValueMult } from '../game/data/upgrades'
import { ensureOrders, matchOrder, orderBonus, type Order } from '../game/orders/orders'
import { applyRound, ensureQuests, isComplete, todayKey, type QuestState } from '../game/quests/quests'
import { addScore, type ScoreEntry } from '../game/scoring/leaderboard'
import type { Grade } from '../game/scoring/scoring'
import { defaultSave, type Counters, type SaveV1 } from '../game/save/saveSchema'
import { clearSave, loadSave } from '../game/save/storage'

export type BuyResult = 'ok' | 'owned' | 'poor' | 'crateOnly' | 'unknown'

export const useProfileStore = defineStore('profile', () => {
  const s = loadSave()
  const money = ref(s.money)
  const totalEarned = ref(s.totalEarned)
  const ownedKnives = ref(s.ownedKnives)
  const equippedKnifeId = ref(s.equippedKnifeId)
  const stats = ref(s.stats)
  const settings = ref(s.settings)
  const unlockedPotatoes = ref(s.unlockedPotatoes)
  const selectedPotatoId = ref(s.selectedPotatoId)
  const upgrades = ref(s.upgrades)
  const orders = ref<Order[]>(s.orders)
  const quests = ref<QuestState | null>(s.quests)
  const leaderboard = ref<ScoreEntry[]>(s.leaderboard)
  const decorOwned = ref<string[]>(s.decorOwned)
  const decorEquipped = ref<string[]>(s.decorEquipped)
  const achievements = ref<string[]>(s.achievements)
  const counters = ref<Counters>(s.counters)
  /** succès à afficher (transitoire, non sauvegardé) */
  const toasts = ref<AchievementDef[]>([])

  const equippedKnife = computed<KnifeDef>(() => knifeById(equippedKnifeId.value) ?? KNIVES[0])
  const owns = (id: string): boolean => !!ownedKnives.value[id]
  const canAfford = (id: string): boolean => {
    const k = knifeById(id)
    return !!k && k.price !== null && money.value >= k.price
  }

  function earn(n: number): void {
    const v = Math.max(0, Math.floor(n))
    money.value += v
    totalEarned.value += v
  }

  function addOwned(id: string): void {
    const cur = ownedKnives.value[id]
    ownedKnives.value[id] = { acquiredAt: cur?.acquiredAt ?? Date.now(), count: (cur?.count ?? 0) + 1 }
  }

  function buy(id: string): BuyResult {
    const k = knifeById(id)
    if (!k) return 'unknown'
    if (owns(id)) return 'owned'
    if (k.price === null) return 'crateOnly'
    if (money.value < k.price) return 'poor'
    money.value -= k.price
    addOwned(id)
    return 'ok'
  }

  function equip(id: string): boolean {
    if (!owns(id)) return false
    equippedKnifeId.value = id
    return true
  }

  function openCrate(rng = createRng(Date.now() >>> 0)): CrateResult | null {
    if (money.value < CRATE_PRICE) return null
    money.value -= CRATE_PRICE
    const res = rollCrate(rng, new Set(Object.keys(ownedKnives.value)))
    bump('crates')
    if (res.duplicate) earn(res.refund)
    else addOwned(res.knifeId)
    return res
  }

  // ---------- patates ----------
  const selectedPotato = computed(() => potatoById(selectedPotatoId.value) ?? POTATOES[0])
  const hasPotato = (id: string): boolean => unlockedPotatoes.value.includes(id)

  function buyPotato(id: string): BuyResult {
    const p = potatoById(id)
    if (!p) return 'unknown'
    if (hasPotato(id)) return 'owned'
    if (money.value < p.price) return 'poor'
    money.value -= p.price
    unlockedPotatoes.value = [...unlockedPotatoes.value, id]
    return 'ok'
  }

  function selectPotato(id: string): boolean {
    if (!hasPotato(id)) return false
    selectedPotatoId.value = id
    return true
  }

  // ---------- décors ----------
  const ownsDecor = (id: string): boolean => decorOwned.value.includes(id)
  const isDecorEquipped = (id: string): boolean => decorEquipped.value.includes(id)

  function buyDecor(id: string): BuyResult {
    const d = decorById(id)
    if (!d) return 'unknown'
    if (ownsDecor(id)) return 'owned'
    if (money.value < d.price) return 'poor'
    money.value -= d.price
    decorOwned.value = [...decorOwned.value, id]
    decorEquipped.value = toggleEquip(decorEquipped.value, id) // équipé dès l'achat
    return 'ok'
  }

  /** Équipe (ou, pour un objet, allume/éteint) un décor possédé. */
  function equipDecor(id: string): boolean {
    if (!ownsDecor(id)) return false
    decorEquipped.value = toggleEquip(decorEquipped.value, id)
    return true
  }

  // ---------- succès ----------
  const achievementContext = computed<AchievementContext>(() => ({
    potatoes: stats.value.potatoes, sGrades: counters.value.sGrades, streak: stats.value.streak, totalEarned: totalEarned.value,
    knivesOwned: Object.keys(ownedKnives.value).length, knivesTotal: KNIVES.length,
    rareKnives: Object.keys(ownedKnives.value).filter((id) => { const r = knifeById(id)?.rarity; return r === 'rare' || r === 'epique' || r === 'legendaire' }).length,
    legendaryKnives: Object.keys(ownedKnives.value).filter((id) => knifeById(id)?.rarity === 'legendaire').length,
    potatoKinds: unlockedPotatoes.value.length, potatoKindsTotal: POTATOES.length,
    upgradeMaxed: Object.entries(upgrades.value).filter(([id, l]) => l >= (upgradeById(id)?.max ?? 99)).length,
    decorOwned: decorOwned.value.length, ordersDelivered: counters.value.ordersDelivered, challenges: counters.value.challenges,
    bestChallengeScore: counters.value.bestChallengeScore, photos: counters.value.photos, crates: counters.value.crates,
  }))

  /** Débloque les succès atteints : récompense créditée et toast mis en file. */
  function checkAchievements(): AchievementDef[] {
    const fresh = newlyUnlocked(achievementContext.value, achievements.value)
    if (!fresh.length) return fresh
    achievements.value = [...achievements.value, ...fresh.map((a) => a.id)]
    for (const a of fresh) earn(a.reward)
    toasts.value = [...toasts.value, ...fresh]
    return fresh
  }

  function dismissToast(): void {
    toasts.value = toasts.value.slice(1)
  }

  const bump = (k: keyof Counters, by = 1): void => {
    counters.value = { ...counters.value, [k]: k === 'bestChallengeScore' ? Math.max(counters.value[k], by) : counters.value[k] + by }
  }

  // ---------- améliorations ----------
  const upgradeLevel = (id: string): number => upgrades.value[id] ?? 0
  const valueMult = computed(() => selectedPotato.value.valueMult * upgradeValueMult(upgrades.value))

  function buyUpgrade(id: string): 'ok' | 'poor' | 'max' | 'unknown' {
    const def = upgradeById(id)
    if (!def) return 'unknown'
    const price = upgradePrice(def, upgradeLevel(id))
    if (price === null) return 'max'
    if (money.value < price) return 'poor'
    money.value -= price
    upgrades.value = { ...upgrades.value, [id]: upgradeLevel(id) + 1 }
    return 'ok'
  }

  // ---------- commandes & quêtes ----------
  function refreshOrders(): void {
    orders.value = ensureOrders(orders.value, createRng(Date.now() >>> 0))
  }

  function refreshQuests(day = todayKey()): void {
    quests.value = ensureQuests(quests.value, day)
  }

  function claimQuest(id: string): number {
    const q = quests.value?.items.find((x) => x.id === id)
    if (!q || q.claimed || !isComplete(q)) return 0
    q.claimed = true
    earn(q.reward)
    return q.reward
  }

  /** Après une manche : quêtes + commande livrée. Renvoie la commande et son bonus (déjà crédité). */
  function recordRound(r: { mode: CutModeId; grade: Grade; reward: number }): { order: Order; bonus: number } | null {
    refreshQuests()
    quests.value = { ...quests.value!, items: applyRound(quests.value!.items, r) }
    refreshOrders()
    const order = matchOrder(orders.value, r.mode, r.grade)
    if (!order) return null
    const bonus = orderBonus(r.reward, order)
    earn(bonus)
    bump('ordersDelivered')
    orders.value = ensureOrders(orders.value.filter((o) => o.id !== order.id), createRng(Date.now() >>> 0))
    return { order, bonus }
  }

  function submitScore(entry: ScoreEntry): number | null {
    const res = addScore(leaderboard.value, entry)
    leaderboard.value = res.list
    return res.rank
  }

  function toSave(): SaveV1 {
    return {
      version: 1, money: money.value, totalEarned: totalEarned.value,
      ownedKnives: ownedKnives.value, equippedKnifeId: equippedKnifeId.value,
      stats: stats.value, settings: settings.value,
      unlockedPotatoes: unlockedPotatoes.value, selectedPotatoId: selectedPotatoId.value, upgrades: upgrades.value,
      orders: orders.value, quests: quests.value, leaderboard: leaderboard.value,
      decorOwned: decorOwned.value, decorEquipped: decorEquipped.value,
      achievements: achievements.value, counters: counters.value,
    }
  }

  /** Remplace tout l'état par une sauvegarde (import fichier / synchro en ligne). */
  function applySave(d: SaveV1): void {
    money.value = d.money
    totalEarned.value = d.totalEarned
    ownedKnives.value = d.ownedKnives
    equippedKnifeId.value = d.equippedKnifeId
    stats.value = d.stats
    settings.value = d.settings
    unlockedPotatoes.value = d.unlockedPotatoes
    selectedPotatoId.value = d.selectedPotatoId
    upgrades.value = d.upgrades
    orders.value = d.orders
    quests.value = d.quests
    leaderboard.value = d.leaderboard
    decorOwned.value = d.decorOwned
    decorEquipped.value = d.decorEquipped
    achievements.value = d.achievements
    counters.value = d.counters
    toasts.value = []
    refreshOrders()
    refreshQuests()
  }

  function resetSave(): void {
    applySave(defaultSave())
    clearSave()
  }

  refreshOrders()
  refreshQuests()
  watch(achievementContext, () => { checkAchievements() }, { deep: true })
  checkAchievements()

  return { applySave, achievements, counters, toasts, achievementContext, checkAchievements, dismissToast, bump, decorOwned, decorEquipped, ownsDecor, isDecorEquipped, buyDecor, equipDecor, unlockedPotatoes, selectedPotatoId, upgrades, orders, quests, leaderboard, selectedPotato, hasPotato, buyPotato, selectPotato, upgradeLevel, valueMult, buyUpgrade, refreshOrders, refreshQuests, claimQuest, recordRound, submitScore, money, totalEarned, ownedKnives, equippedKnifeId, stats, settings, equippedKnife, owns, canAfford, earn, buy, equip, openCrate, toSave, resetSave }
})
